import { useEffect, useMemo, useState } from "react";
import { Euro, Users, CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";

import { DateRangeFilter } from "@/components/filters/DateRangeFilter";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import {
  fetchCustomers,
  fetchOrders,
  enrichOrdersWithCustomers,
  getOrders,
  isDateWithinRange,
  saveOrders,
  type ClientRecord,
  type OrderRecord,
} from "@/lib/utils";
import { getAuthenticatedUser } from "@/lib/auth";

import { KPICard } from "./KPICard";
import { OverviewChart } from "./OverviewChart";
import { RecentOrdersList } from "./RecentOrdersList";
import {
  buildMonthlyRevenueData,
  formatCurrencyValue,
  formatDeltaText,
  getDashboardMetrics,
  getPreviousDateRange,
} from "./dashboardMetrics";

// ----------------------------------------------------------------------------

interface DashboardProps {
  className?: string;
}

export function Dashboard(props: DashboardProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { className } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [startDate, setStartDate] = useState(`${currentYear - 1}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear + 1}-12-31`);
  const [orders, setOrders] = useState<OrderRecord[]>(() => getOrders());
  const [customers, setCustomers] = useState<ClientRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentUser = useMemo(() => getAuthenticatedUser(), []);
  const canViewAll = currentUser?.canViewAll || currentUser?.id?.toLowerCase() === "dev-nogravity";
  const agentTag = currentUser?.agentTag;
  const agentCode = currentUser?.agentCode;
  const activeAgent = canViewAll ? "" : (agentTag || (agentCode ? `agente-${agentCode}` : "none"));

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    let isMounted = true;

    async function loadDashboardData(): Promise<void> {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [apiCustomers, apiOrders] = await Promise.all([
          fetchCustomers(activeAgent),
          fetchOrders(undefined, activeAgent),
        ]);

        if (!isMounted) {
          return;
        }

        const isStoredenOrderId = (id: string) =>
          /^\d+_[a-z0-9]+$/i.test(id) || /^\d{4,}$/.test(id);

        const localOrders = getOrders();
        const apiOrderIds = new Set(apiOrders.map((order) => order.id));
        const localOnlyOrders = localOrders.filter((order) => {
          const s = Number(order.status);
          if (s === 6 || s === 7) return false;
          return !isStoredenOrderId(order.id) && !apiOrderIds.has(order.id);
        });
        const rawMergedOrders = [...localOnlyOrders, ...apiOrders];
        const mergedOrders = enrichOrdersWithCustomers(rawMergedOrders, apiCustomers);

        setCustomers(apiCustomers);
        setOrders(mergedOrders);
        saveOrders(mergedOrders);
      } catch (error) {
        console.error("[Dashboard] Data fetch failed:", error);

        if (!isMounted) {
          return;
        }

        setCustomers([]);
        setOrders(getOrders());
        setErrorMessage(
          "Impossibile caricare tutti i dati live. La dashboard sta usando gli ordini salvati in locale.",
        );
        toast.warning("Dashboard in modalita fallback", {
          id: "dashboard-fallback-warning",
          description: "Impossibile contattare il server ngrok.",
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, [activeAgent]);

  // -------------------------------------
  // Component functions
  // -------------------------------------

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const agentScopedCustomers = useMemo(() => {
    if (canViewAll) return customers.filter((c) => !c.isAgent);
    if (!agentTag && !agentCode) return [];
    const tagLower = agentTag ? agentTag.toLowerCase().trim() : "";
    const codeStr = agentCode ? agentCode.trim() : "";

    return customers.filter((c) => {
      if (c.isAgent === true) return false;
      const tags = (c.tags ?? []).map((t) => t.toLowerCase().trim());
      return tags.some((t) => {
        if (tagLower && t === tagLower) return true;
        if (codeStr && (t === `agente-${codeStr}` || t === `agent-${codeStr}`)) return true;
        return false;
      });
    });
  }, [customers, canViewAll, agentTag, agentCode]);

  const agentScopedOrders = useMemo(() => {
    if (canViewAll) return orders;
    if (!agentTag && !agentCode) return [];
    const ids = new Set(agentScopedCustomers.map((c) => c.id.toLowerCase()));
    const emails = new Set(agentScopedCustomers.map((c) => c.email.toLowerCase()));
    const names = new Set(agentScopedCustomers.map((c) => c.name.toLowerCase()));
    if (currentUser?.fullName) names.add(currentUser.fullName.toLowerCase());

    return orders.filter((o) => {
      const matchId = o.clientId && ids.has(o.clientId.toLowerCase());
      const matchEmail = o.email && emails.has(o.email.toLowerCase());
      const matchName = o.customerName && names.has(o.customerName.toLowerCase());
      return matchId || matchEmail || matchName;
    });
  }, [orders, agentScopedCustomers, canViewAll, agentTag, agentCode, currentUser]);

  const filteredOrders = useMemo(
    () =>
      agentScopedOrders.filter((order) =>
        isDateWithinRange(order.date, startDate, endDate),
      ),
    [agentScopedOrders, endDate, startDate],
  );

  const previousDateRange = useMemo(
    () => getPreviousDateRange({ startDate, endDate }),
    [endDate, startDate],
  );

  const previousPeriodOrders = useMemo(
    () =>
      agentScopedOrders.filter((order) =>
        isDateWithinRange(
          order.date,
          previousDateRange.startDate,
          previousDateRange.endDate,
        ),
      ),
    [agentScopedOrders, previousDateRange.endDate, previousDateRange.startDate],
  );

  const currentMetrics = useMemo(
    () => getDashboardMetrics(filteredOrders),
    [filteredOrders],
  );

  const previousMetrics = useMemo(
    () => getDashboardMetrics(previousPeriodOrders),
    [previousPeriodOrders],
  );

  const chartData = useMemo(
    () => buildMonthlyRevenueData(filteredOrders, { startDate, endDate }),
    [endDate, filteredOrders, startDate],
  );

  const customersTrend =
    agentScopedCustomers.length > 0
      ? `${agentScopedCustomers.length} clienti gestiti in anagrafica`
      : formatDeltaText(
          currentMetrics.activeClientsCount,
          previousMetrics.activeClientsCount,
          "Nessun cliente attivo nel periodo",
        );

  return (
    <DashboardLayout className={className}>
      <div className="w-full">
        <PageBreadcrumb
          items={[{ label: "Home", to: "/" }, { label: "Dashboard" }]}
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            {currentUser?.fullName && (
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>Bentornato/a, <strong className="text-gray-900 font-semibold">{currentUser.fullName}</strong></span>
                {currentUser.role && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200">
                    {currentUser.role}
                  </span>
                )}
              </p>
            )}
          </div>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </div>

        {errorMessage && (
          <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {errorMessage}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard
            title="Totale fatturato"
            value={formatCurrencyValue(currentMetrics.revenue)}
            trend={formatDeltaText(
              currentMetrics.revenue,
              previousMetrics.revenue,
              "Nessun fatturato nel periodo",
            )}
            icon={<Euro className="h-5 w-5" />}
            isLoading={isLoading}
          />
          <KPICard
            title="Clienti attivi"
            value={String(currentMetrics.activeClientsCount)}
            trend={customersTrend}
            icon={<Users className="h-5 w-5" />}
            isLoading={isLoading}
          />
          <KPICard
            title="Ticket medio"
            value={formatCurrencyValue(currentMetrics.averageOrderValue)}
            trend={formatDeltaText(
              currentMetrics.averageOrderValue,
              previousMetrics.averageOrderValue,
              "Nessun ticket medio disponibile",
            )}
            icon={<CreditCard className="h-5 w-5" />}
            isLoading={isLoading}
          />
          <KPICard
            title="Ordini"
            value={String(currentMetrics.ordersCount)}
            trend={formatDeltaText(
              currentMetrics.ordersCount,
              previousMetrics.ordersCount,
              "Nessun ordine nel periodo",
            )}
            icon={<FileText className="h-5 w-5" />}
            isLoading={isLoading}
          />
        </div>

        {/* Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <OverviewChart
            className="lg:col-span-2"
            data={chartData}
            isLoading={isLoading}
          />
          <RecentOrdersList
            className="lg:col-span-1"
            startDate={startDate}
            endDate={endDate}
            orders={agentScopedOrders}
            isLoading={isLoading}
            errorMessage={errorMessage}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
